<template>
  <Modal
    class="scoped"
    full-height
    :modal-style="{ maxWidth: '900px' }"
    :title="`Restore TV Content from (${broadcastRecorderHost})`"
  >
    <QCard class="full-width">
      <QCardSection>
        <div class="text-h6 q-mb-md">Seelct Date Range:</div>

        <div class="q-gutter-y-md">
          <!-- Date Range Picker -->
          <div class="date-picker-container">
            <InputDatePicker
              v-model:range="dateRange"
              action-button-label="Apply"
              bordered
              date-style="medium"
              limit-to-past-year
              range-selection="custom"
              show-date-input
              show-range-options
              show-time-input
              show-update-button
              time-style="short"
            />
          </div>

          <div class="text-h6 q-mb-md">Select Content Type:</div>
          <div>
            <QSelect
              v-model="contentType"
              emit-value
              map-options
              :options="contentTypeOptions"
              outlined
              style="min-width: 250px; max-width: 300px"
            />
          </div>

          <div class="q-mt-md">
            <QCheckbox
              v-model="forceReplace"
              label="Force Replace"
            />
            <div class="text-caption text-grey-7 q-ml-lg">
              When enabled, we remove the existing clips before loading the new
              ones
            </div>
          </div>
        </div>
      </QCardSection>

      <QCardActions>
        <div class="full-width flex justify-center">
          <div>
            <QBtn
              v-close-popup
              color="black"
              :label="`Confirm Restore from (${broadcastRecorderHost})`"
              no-caps
              @click="restoreContent"
            />
          </div>
        </div>
      </QCardActions>
    </QCard>
  </Modal>
</template>

<script setup lang="ts">
import { useQuasar } from "quasar";
import { onMounted, ref } from "vue";

import { $streemApiAdmin } from "shared/boot/api";
import InputDatePicker from "shared/components/base/InputDatePicker.vue";
import Modal from "shared/components/core/modals/Modal.vue";
import useModal from "shared/composables/useModal";
import DateRange from "shared/helpers/DateRange";

interface Props {
  channelId: number;
  broadcastRecorderHost: string;
}

const props = defineProps<Props>();
const dateRange = ref<DateRange | null>(null);
const contentType = ref<string>("tv_files");

const contentTypeOptions = ref([
  { label: "TV Files", value: "tv_files" },
  { label: "TV Caption", value: "tv_caption" },
  { label: "TV Super", value: "tv_super" },
]);

const forceReplace = ref<boolean>(false);
const { close: closeModal } = useModal("TvClipRestoreModal");
const $q = useQuasar();

onMounted(() => {
  const now = new Date();
  const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);

  dateRange.value = DateRange.fromDates(oneHourAgo, now);
});

async function restoreContent() {
  if (!dateRange.value || !dateRange.value.after || !dateRange.value.before) {
    $q.notify({
      color: "negative",
      message: "Please select a valid date range",
    });

    return;
  }

  try {
    const params = {
      channel_id: props.channelId,
      after: dateRange.value.after,
      before: dateRange.value.before,
      content_type: contentType.value,
      replace: forceReplace.value,
    };

    await $streemApiAdmin.post(`tv_channels/${props.channelId}/restore`, {
      params,
    });

    closeModal();

    $q.dialog({
      title: `Restoring TV Content from (${props.broadcastRecorderHost})`,
    });
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";

    $q.notify({
      color: "negative",
      message: `Failed to restore content: ${errorMessage}`,
    });
  }
}
</script>
